import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import type { DateRangeOption } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { ContributionAnalyticsDetails, type ContributionAnalyticsDetailsProps } from "./ContributionAnalyticsDetails";

export function ContributionDetailPage(): React.ReactNode {
  const { t } = useTranslation();

  const dateRangeOptions = useMemo<NonEmptyArray<DateRangeOption>>(
    () => [
      {
        name: t("common.date-range.last-months", { count: 6 }),
        value: "last6Months",
      },
      {
        name: t("common.date-range.last-year"),
        value: "lastYear",
      },
    ],
    [t],
  );

  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const queries = useAnalyticsQueries();
  const {
    data: contributionData,
    isPending: isPendingContributionData,
    error: contributionError,
  } = useQuery(queries.contribution(dateRange.value, { withCurrentMonth: true }));

  const error = contributionError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const contentProps: ContributionAnalyticsDetailsProps = {
    contribution: contributionData,
    isLoading: isPendingContributionData,
    selectedDateRange: dateRange,
    dateRangeOptions,
    onDateRangeChange: setDateRange,
  };

  return <ContributionAnalyticsDetails {...contentProps} />;
}
